import React, { useRef, useState } from 'react'
import { useDialogFocus } from '../../shared/organization-deletion/dialog-focus'
import { failureText } from './api'
import { LmsIcon } from './lms-icon'
import './lms-admin.scss'

// The reason a person gives for sending something to the LMS again: the
// service takes 3 to 500 characters and keeps it with the audit entry.
export const REASON_MIN = 3
export const REASON_MAX = 500

export const reasonProblem = text => {
  const length = text.trim().length
  if (length < REASON_MIN) return `Say why in at least ${REASON_MIN} characters.`
  if (length > REASON_MAX) return `Keep it to ${REASON_MAX} characters or fewer.`
  return null
}

// A dialog that asks "Why are you resending?" and posts it. `send(reason)` does
// the write and resolves to the service's answer; `outcome(answer)` says in words
// what it did. After a send the dialog shows that and a Close button; `onDone`
// runs once with the answer (to reload the page behind it).
export const ResendDialog = ({ title, intro, sendLabel, failureFallback, send, outcome, onDone, onClose, testId }) => {
  const [reason, updateReason] = useState('')
  const [touched, updateTouched] = useState(false)
  const [busy, updateBusy] = useState(false)
  const [result, updateResult] = useState(null)
  const [error, updateError] = useState(null)
  const dialog = useRef(null)
  const field = useRef(null)
  useDialogFocus(true, dialog, field)

  const problem = reasonProblem(reason)
  const submit = async event => {
    event.preventDefault()
    if (busy || result) return
    updateTouched(true)
    if (problem) return
    updateBusy(true)
    updateError(null)
    try {
      const answer = await send(reason.trim())
      updateResult(answer || {})
      if (onDone) onDone(answer)
    } catch (e) {
      updateError(e)
    } finally {
      updateBusy(false)
    }
  }

  return (
    <div className="lms-admin">
      <div className="lms-modal-scrim" data-testid={testId}>
        <form className="lms-modal" role="dialog" aria-modal="true" aria-label={title} ref={dialog} onSubmit={submit} noValidate>
          <div className="modal-head">
            <div className="icon">
              <LmsIcon name="rotate" />
            </div>
            <div className="body">
              <h2>{title}</h2>
            </div>
            <button className="close-btn" title="Close" type="button" onClick={onClose}>
              <LmsIcon name="xmark" />
            </button>
          </div>
          <div className="lms-modal-body">
            {result ? (
              <div data-testid="resend-outcome">
                <LmsIcon name="check" className="me-1" />
                {outcome(result)}
              </div>
            ) : (
              <>
                <p className="field-help" style={{ marginTop: 0 }}>
                  {intro}
                </p>
                <label htmlFor="resend-reason" className="label">
                  Why are you resending?
                </label>
                <textarea
                  id="resend-reason"
                  ref={field}
                  className="form-control"
                  rows={3}
                  maxLength={REASON_MAX + 100}
                  value={reason}
                  disabled={busy}
                  aria-invalid={touched && !!problem}
                  onChange={e => updateReason(e.target.value)}
                />
                <div className="field-help" data-testid="resend-count">
                  {reason.trim().length}/{REASON_MAX}
                </div>
                {touched && problem && (
                  <div className="field-error" data-testid="resend-reason-error">
                    <LmsIcon name="circle-exclamation" />
                    {problem}
                  </div>
                )}
                {error && (
                  <div className="field-error" data-testid="resend-error" role="alert">
                    <LmsIcon name="circle-exclamation" />
                    {failureText(error, failureFallback)}
                  </div>
                )}
              </>
            )}
          </div>
          <div className="modal-foot">
            <span className="help" />
            {result ? (
              <button className="btn btn-primary btn-sm" type="button" data-testid="resend-close" onClick={onClose}>
                Close
              </button>
            ) : (
              <>
                <button className="btn btn-light border btn-sm" type="button" data-testid="resend-cancel" onClick={onClose}>
                  Cancel
                </button>
                <button className="btn btn-primary btn-sm" type="submit" disabled={busy} data-testid="resend-confirm">
                  {busy ? 'Resending…' : sendLabel}
                </button>
              </>
            )}
          </div>
        </form>
      </div>
    </div>
  )
}
